import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWindowSize } from '../components/useWindowSize';
import axios from 'axios';

const CURRENCY_CONFIG = [
  { id: 'USDINR',  label: 'USD/INR', comp: 'USDINRCOMP' },
  { id: 'EURINR',  label: 'EUR/INR', comp: 'EURINRCOMP' },
  { id: 'GBPINR',  label: 'GBP/INR', comp: 'GBPINRCOMP' },
  { id: 'JPYINR',  label: 'JPY/INR', comp: 'JPYINRCOMP' },
  { id: 'AUDINR',  label: 'AUD/INR', comp: 'AUDINRCOMP' },
  { id: 'AEDINR',  label: 'AED/INR', comp: null },
  { id: 'EURUSD',  label: 'EUR/USD', comp: null },
  { id: 'GBPUSD',  label: 'GBP/USD', comp: null },
];

export default function Dashboard() {
  const { API, user }           = useAuth();
  const isMobile                = useWindowSize();
  const [rates, setRates]       = useState({});
  const [selected, setSelected] = useState(['USDINR', 'EURINR']);
  const [live, setLive]         = useState(false);
  const [lastUp, setLastUp]     = useState(null);

  useEffect(() => {
    fetchRates();
    const iv = setInterval(fetchRates, 333);
    return () => clearInterval(iv);
  }, []);

  const fetchRates = async () => {
    try {
      const res = await axios.get(API + '/rates/live');
      const map = {};
      res.data.forEach(r => { map[r.currencyPair] = r; });
      setRates(map);
      setLive(true);
      setLastUp(new Date());
    } catch {
      setLive(false);
    }
  };

  const toggleCurrency = (id) => {
    setSelected(prev =>
      prev.includes(id)
        ? prev.filter(p => p !== id)
        : [...prev, id]
    );
  };

  const displayPairs = CURRENCY_CONFIG.filter(c => selected.includes(c.id));
  const filterCols   = isMobile ? 'repeat(4, 1fr)' : 'repeat(8, auto)';

  return (
    <div style={{ minHeight: '100vh', background: '#F0F4F8', paddingBottom: '80px' }}>

      {/* HEADER */}
      <div style={{
        background: '#1B3A6B',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px', height: '36px',
              background: '#D4A843', borderRadius: '8px',
              display: 'flex', alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Arial', fontSize: '14px',
              fontWeight: 700, color: '#1B3A6B',
            }}>FX</div>
            <div>
              <div style={{ fontFamily: 'Arial', fontSize: '13px', fontWeight: 700, color: '#D4A843', letterSpacing: '0.06em' }}>
                FX CAPITAL INDIA
              </div>
              <div style={{ fontFamily: 'Arial', fontSize: '10px', color: '#93C5FD' }}>
                Live Rates
              </div>
            </div>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '20px', padding: '5px 10px',
          }}>
            <div style={{
              width: '7px', height: '7px', borderRadius: '50%',
              background: live ? '#4ADE80' : '#F87171',
            }} />
            <span style={{ fontSize: '10px', fontWeight: 500, color: live ? '#4ADE80' : '#F87171' }}>
              {live ? 'LIVE' : 'OFF'}
            </span>
            {lastUp && (
              <span style={{ fontFamily: 'monospace', fontSize: '10px', color: '#CBD5E1' }}>
                {lastUp.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* CURRENCY FILTER */}
      <div style={{ background: '#fff', padding: '10px 16px', borderBottom: '1px solid #E5E7EB' }}>
        <div style={{ fontFamily: 'Arial', fontSize: '10px', fontWeight: 500, color: '#6B7280', letterSpacing: '0.08em', marginBottom: '8px' }}>
          SELECT CURRENCIES
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: filterCols, gap: '6px' }}>
          {CURRENCY_CONFIG.map(c => {
            const isOn = selected.includes(c.id);
            return (
              <button
                key={c.id}
                onClick={() => toggleCurrency(c.id)}
                style={{
                  fontFamily: 'Arial', fontSize: '11px',
                  padding: '7px 8px', borderRadius: '8px',
                  border: '0.5px solid',
                  cursor: 'pointer', textAlign: 'center',
                  background:  isOn ? '#1B3A6B' : '#F3F4F6',
                  color:       isOn ? '#FFFFFF' : '#374151',
                  borderColor: isOn ? '#1B3A6B' : '#E5E7EB',
                  fontWeight:  isOn ? 600 : 400,
                  minWidth:    isMobile ? 'auto' : '80px',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '3px',
                }}>
                {c.label}
                {isOn && <span style={{ fontSize: '9px' }}>✓</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* RATE CARDS */}
      <div style={{ padding: isMobile ? '12px' : '20px 24px' }}>
        {displayPairs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF', fontFamily: 'Arial', fontSize: '13px' }}>
            Tap a currency above to view rates
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {displayPairs.map(cfg => (
              <PairRow
                key={cfg.id}
                cfg={cfg}
                rates={rates}
                isMobile={isMobile}
              />
            ))}
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', fontSize: '10px', color: '#9CA3AF', fontFamily: 'Arial', padding: '12px 16px' }}>
        Rates are indicative only. Contact FX Capital India for transaction rates.
      </div>
    </div>
  );
}

// ── PAIR ROW — shows CCIL + Market side by side ──
function PairRow({ cfg, rates, isMobile }) {
  const marketRate = rates[cfg.id];
  const ccilRate   = cfg.comp ? rates[cfg.comp] : null;

  // If both exist — show side by side on desktop, stacked on mobile
  if (ccilRate && marketRate) {
    return (
      <div style={{
        display: isMobile ? 'flex' : 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: '12px',
      }}>
        <div style={{ flex: 1 }}>
          <RateCard rate={ccilRate}   pair={cfg.id} type="CCIL Rate"   typeColor="#D97706" isMobile={isMobile} />
        </div>
        <div style={{ flex: 1 }}>
          <RateCard rate={marketRate} pair={cfg.id} type="Market Rate" typeColor="#1B3A6B" isMobile={isMobile} />
        </div>
      </div>
    );
  }

  // Only market rate
  return (
    <RateCard rate={marketRate} pair={cfg.id} type="Market Rate" typeColor="#1B3A6B" isMobile={isMobile} />
  );
}

// ── RATE CARD ──
function RateCard({ rate, pair, type, typeColor, isMobile }) {
  const pct  = rate?.pctChange  || 0;
  const net  = rate?.netChange  || 0;
  const isUp = pct >= 0;
  const chgColor = isUp ? '#16A34A' : '#DC2626';
  const chgBg    = isUp ? '#F0FDF4' : '#FEF2F2';
  const chgBorder= isUp ? '#BBF7D0' : '#FECACA';

  const fmt = (v, dp=4) => {
    try { return parseFloat(v).toFixed(dp); }
    catch { return '--'; }
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: '12px',
      border: '0.5px solid #E5E7EB',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      overflow: 'hidden',
    }}>
      {/* TOP BAR */}
      <div style={{
        background: '#1B3A6B',
        padding: '10px 14px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div>
          <div style={{ fontFamily: 'Arial', fontSize: '10px', fontWeight: 600, color: '#93C5FD', letterSpacing: '0.08em', marginBottom: '2px' }}>
            {type.toUpperCase()}
          </div>
          <div style={{ fontFamily: 'Arial', fontSize: '18px', fontWeight: 700, color: '#FFFFFF', letterSpacing: '0.04em' }}>
            {pair.replace('COMP', '').replace('INR', ' INR').replace('USD', ' USD').replace('EUR', 'EUR').replace('GBP', 'GBP')}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 600, color: '#CBD5E1', marginBottom: '4px' }}>
            LTP {rate ? fmt(rate.ltp) : '--'}
          </div>
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '11px', color: chgColor, fontWeight: 600 }}>
              {net >= 0 ? '+' : ''}{fmt(net, 4)}
            </span>
            <span style={{
              background: chgBg, color: chgColor,
              border: '0.5px solid ' + chgBorder,
              fontFamily: 'monospace', fontSize: '11px',
              fontWeight: 700, padding: '2px 8px',
              borderRadius: '10px',
            }}>
              {isUp ? '+' : ''}{fmt(pct, 2)}%
            </span>
          </div>
        </div>
      </div>

      {/* BID / ASK */}
      <div style={{ display: 'flex', borderBottom: '1px solid #F3F4F6' }}>
        <div style={{ flex: 1, padding: '14px', textAlign: 'center', background: '#F8FAFC' }}>
          <div style={{ fontFamily: 'Arial', fontSize: '9px', fontWeight: 700, color: '#6B7280', letterSpacing: '0.12em', marginBottom: '6px' }}>
            BID
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: isMobile ? '26px' : '28px', fontWeight: 600, color: '#16A34A' }}>
            {rate ? fmt(rate.bid) : '--'}
          </div>
        </div>
        <div style={{ width: '1px', background: '#E5E7EB' }} />
        <div style={{ flex: 1, padding: '14px', textAlign: 'center', background: '#F8FAFC' }}>
          <div style={{ fontFamily: 'Arial', fontSize: '9px', fontWeight: 700, color: '#6B7280', letterSpacing: '0.12em', marginBottom: '6px' }}>
            ASK
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: isMobile ? '26px' : '28px', fontWeight: 600, color: '#DC2626' }}>
            {rate ? fmt(rate.ask) : '--'}
          </div>
        </div>
      </div>

      {/* OHLC */}
      <div style={{ display: 'flex', padding: '10px 0' }}>
        {[
          ['Open',  rate?.open,  '#1D4ED8'],
          ['High',  rate?.high,  '#16A34A'],
          ['Low',   rate?.low,   '#DC2626'],
          ['Close', rate?.close, '#1B3A6B'],
        ].map(([label, val, color]) => (
          <div key={label} style={{ flex: 1, textAlign: 'center', borderRight: '1px solid #F3F4F6' }}>
            <div style={{ fontFamily: 'Arial', fontSize: '9px', fontWeight: 600, color: color, letterSpacing: '0.06em', marginBottom: '3px' }}>
              {label}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: isMobile ? '11px' : '12px', color: '#374151', fontWeight: 500 }}>
              {val ? fmt(val) : '--'}
            </div>
          </div>
        ))}
      </div>

      {/* TIME */}
      <div style={{ padding: '4px 14px 8px', textAlign: 'right' }}>
        <span style={{ fontFamily: 'monospace', fontSize: '10px', color: '#9CA3AF' }}>
          {rate?.marketTime || '--'}
        </span>
      </div>
    </div>
  );
}